import { OnInit, Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {
    AzavistaApiService, IPagedResponse, ISearchEventsRequest, ISearchContactsRequest, ISearchOrganizationsRequest
} from '@azavista/servicelib';
import { AbstractWidgetDirective } from '@azavista/components/widgets';
import { IField, IFilter } from '@azavista/components/shared';
import { IObjectCountSettings, CountType } from './interfaces';
import { ObjectCountWidgetService } from './object-count-widget.service';
import { IFilterCriteriaData, AzavistaFilterCriteriaComponent } from '@azavista/components/filter-criteria';
import { SharedService } from '../../shared.service';
import { IWidgetDataChanges, WidgetViewDetailsNavigationPaths } from '../interfaces';
import { TranslationKey } from '../..';

@Component({
    templateUrl: 'object-count-widget.component.html',
    styleUrls: ['object-count-widget.component.css']
})
export class ObjectCountWidgetComponent extends AbstractWidgetDirective implements OnInit {

    filterFields: IField[];
    definitionSettings: IObjectCountSettings;

    constructor(
        private apiSvc: AzavistaApiService, private objectCountWidgetSvc: ObjectCountWidgetService,
        private sharedSvc: SharedService, private matDialog: MatDialog
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.definition.data = null;
        this.definitionSettings = this.definition.settings as IObjectCountSettings;
        const hasRemoteSettings = !this.objectCountWidgetSvc.areRemoteSettingsEmpty(this.definitionSettings.remote);
        if (hasRemoteSettings) {
            // Start loading on next loop because it causes loading output to be emitted which could cause
            // Angular change detection errors in the consumer (the dashboard component) if it changes template bound variables
            // in the event handler
            if (!this.configMode) {
                window.setTimeout(() => this.loadWidgetData());
            }
        } else {
            // Notify parent that we need remote settings
            this.settingsNeeded.next();
        }
        super.subscribeToDataChanges({
            next: (newData?: IWidgetDataChanges) => {
                this.dataChanged(newData);
            }
        });
        if (this.configMode) {
            await this.manageConfigMode();
        }
        this.setCustomWidgetParams();
    }

    setCustomWidgetParams(): void {
        const countType = this.definition.settings.local.countType;
        switch (countType) {
            case CountType.organizations:
                this.definition.viewButtonText = this.sharedSvc.translate(TranslationKey.goToOrganizationsList);
                this.definition.viewDetailsNavPath = WidgetViewDetailsNavigationPaths.crmOrganizations;
                break;
            case CountType.events:
                this.definition.viewButtonText = this.sharedSvc.translate(TranslationKey.goToEventsList);
                this.definition.viewDetailsNavPath = WidgetViewDetailsNavigationPaths.eventsList;
                break;
            default:
                this.definition.viewButtonText = this.sharedSvc.translate(TranslationKey.goToContactsList);
                this.definition.viewDetailsNavPath = WidgetViewDetailsNavigationPaths.crmContacts;
        }
        this.emitSettingsChanged();
    }

    dataChanged(newData?: IWidgetDataChanges): void {
        if (!this.configMode && newData.settings && newData.settings.remote) {
            // Not config mode and remote settings were changed - reload data from server
            this.loadWidgetData();
        }
    }

    async loadWidgetData(): Promise<void> {
        this.definitionSettings = this.definition.settings;
        try {
            this.notifyLoading(true);
            const pagedResponse = await this.requestServer();
            if (pagedResponse) {
                this.processDataLoaded(pagedResponse);
            }
        } finally {
            this.notifyLoading(false);
        }
    }

    async requestServer(): Promise<IPagedResponse> {
        const countType = this.definitionSettings.local.countType;
        let pagedResponse: IPagedResponse;
        if (countType === CountType.events) {
            const searchEventsReq: ISearchEventsRequest = {
                limit: 1, offset: 0, advancedQuery: this.definitionSettings.remote.filter
            };
            pagedResponse = await this.apiSvc.searchEvents(searchEventsReq);
        } else if (countType === CountType.contacts) {
            const searchContactsReq: ISearchContactsRequest = {
                limit: 1, offset: 0, advancedQuery: this.definitionSettings.remote.filter
            };
            pagedResponse = await this.apiSvc.searchContacts(searchContactsReq);
        } else if (countType === CountType.organizations) {
            const searchOrgsReq: ISearchOrganizationsRequest = {
                limit: 1, offset: 0, advancedQuery: this.definitionSettings.remote.filter
            };
            pagedResponse = await this.apiSvc.searchOrganizations(searchOrgsReq);
        }
        return pagedResponse;
    }

    processDataLoaded(pagedResponse: IPagedResponse): void {
        this.definition.data = pagedResponse.object_count;
        this.definition.dataChangedSubject.next({ data: this.definition.data });
    }

    async manageConfigMode(): Promise<void> {
        this.setDefaultSettingsIfMissing();
        await this.loadFilterFields();
        this.emitSettingsChanged();
    }

    async loadFilterFields(): Promise<void> {
        this.filterFields = null;
        const countType = this.definitionSettings.local.countType;
        this.filterFields = countType ? await this.getFields(countType) : null;
    }

    onShowFilter(): void {
        const dialogData: IFilterCriteriaData = {
            allFilterNames: [],
            allFields: this.filterFields,
            filter: { id: 'dummy-id', name: 'dummy-name', criteria: [] },
            isNew: true, hideFilterName: true,
            searchParams: this.definitionSettings.remote.filter || {}
        } as IFilterCriteriaData;
        const dialogRef = this.matDialog.open(AzavistaFilterCriteriaComponent, {
            data: dialogData,
            panelClass: 'filter-criteria-dialog',
        });
        const deleteSubscription = dialogRef.componentInstance.deleted.subscribe({
            next: () => {
                deleteSubscription.unsubscribe();
                this.definitionSettings.remote.filter = {};
                dialogRef.close();
            }
        });
        dialogRef.afterClosed().subscribe({
            next: dialogResult => {
                deleteSubscription.unsubscribe();
                this.processEditFilterDialogResult(dialogResult);
            }
        });

    }

    processEditFilterDialogResult(dialogResult: IFilter): void {
        if (!dialogResult) {
            return;
        }
        const searchParams = this.sharedSvc.createSearchParams(dialogResult.criteria);
        this.definitionSettings.remote.filter = searchParams;
    }

    setDefaultSettingsIfMissing(): void {
        this.definitionSettings.remote = this.definitionSettings.remote || { filter: {} };
        this.definitionSettings.remote.filter = this.definitionSettings.remote.filter || {};
        this.definitionSettings.local = this.definitionSettings.local || { countType: '' as CountType };
    }

    async getFields(countType: CountType): Promise<IField[]> {
        let fields: IField[];
        if (countType === CountType.events) {
            fields = await this.apiSvc.getAllGlobalEventFields();
        } else if (countType === CountType.contacts) {
            fields = await this.apiSvc.getAllContactFields();
        } else if (countType === CountType.organizations) {
            fields = await this.apiSvc.getAllOrganizationFields();
        }
        return fields;
    }

    async onCountTypeChanged(): Promise<void> {
        await this.loadFilterFields();
        this.setCustomWidgetParams();
        this.emitSettingsChanged();
    }

    emitSettingsChanged(): void {
        const settingsAreValid = this.objectCountWidgetSvc.areSettingsValid(this.definitionSettings);
        this.settingsChanged.next({
            definition: this.definition,
            settings: this.definitionSettings,
            settingsAreValid: settingsAreValid
        });
    }
}
